<template>
  <div class="new-password__informations">
    <div class="flex">
      <span
        v-for="i in passwordStrengthLength"
        :key="`passwordStrengthClasses_${i}`"
        :class="['new-password__informations--steps', getPasswordStrengthClasses(i)]"
      >&nbsp;</span>
    </div>

    <div class="new-password__tips">
      <p class="new-password__tips--text">{{ strengthValidationTitle }}</p>
      <Tooltip :message="this.tooltipMessage">
        <svg class="ico">
          <use xlink:href="/images/svg-symbols.svg#info"></use>
        </svg>
      </Tooltip>
    </div>

    <div class="new-password__validations">
      <p
        v-for="field in validationFields"
        :key="field.id"
        class="new-password__validation-item"
        :class="validationClasses(field.status)"
      >
        {{ field.message }}
      </p>
    </div>
  </div>
</template>

<script>
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import Tooltip from "commons/js/components/atoms/Tooltip.vue";

const PASSWORD_STRENGTH_LEVEL = {
  None: 0,
  Weak: 1,
  Regular: 2,
  Good: 3,
  Great: 4,
};

export default {
  components: {
    Tooltip,
  },

  props: {
    password: {
      type: String,
      required: true,
      default: '',
    },

    minPasswordLength: {
      type: Number,
      default: 12,
    },

    maxPasswordLength: {
      type: Number,
      default: 64,
    },
  },

  data() {
    return {
      strengthLength: {
        weak: 12,
        strong: 18,
      },
      validationFields: [
        {
          id: 1,
          validator: (value) => value.length >= this.minPasswordLength && value.length <= this.maxPasswordLength,
          message: `Use entre ${this.minPasswordLength} e ${this.maxPasswordLength} caracteres`,
          status: null,
        },
        {
          id: 2,
          validator: (value) => !/(.)\1{2}/.test(value),
          message: 'Não use repetições como AAA, BBB, $$$',
          status: null,
        },
        {
          id: 3,
          validator: (value) => !this.containsSequence(value),
          message: 'Não use sequências como 123, abc',
          status: null,
        },
      ],
      passwordLevel: PASSWORD_STRENGTH_LEVEL.None,
      passwordStrengthLength: 4,
    };
  },

  computed: {
    strengthValidationTitle() {
      if (this.passwordLevel === PASSWORD_STRENGTH_LEVEL.None) {
        return 'Crie uma senha forte';
      }

      const strengthLabels = ['fraca', 'regular', 'boa', 'ótima'];

      return `Força da senha: ${strengthLabels[this.passwordLevel - 1]}`;
    },

    tooltipMessage() {
      const passwordLengthMessage = "Prefira frases longas com 4 ou mais palavras,";
      const otherTipsMessage = "sem incluir seu nome, telefone, data de nascimento ou termos óbvios como melhorenvio.";

      const articleLink = "https://ajuda.melhorenvio.com.br/pt-BR/articles/5802487-saiba-como-cadastrar-uma-senha-forte-para-a-sua-conta-no-melhor-envio";
      const learnMoreLink = `<a href='${articleLink}' target="_blank" rel="noopener noreferrer">Veja como criar uma senha forte</a>`;

      return `${passwordLengthMessage} ${otherTipsMessage} ${learnMoreLink}`;
    },
  },

  methods: {
    validationClasses(status) {
      if (status === null) {
        return '';
      }

      return status
        ? 'new-password__validation-item--correct'
        : 'new-password__validation-item--error';
    },

    getPasswordStrengthClasses(i) {
      return {
        'bg-danger': i === 1 && this.passwordLevel === 1,
        'bg-neutral-light': (i === 1 && this.passwordLevel === 0)
          || (i === 2 && this.passwordLevel < 2)
          || (i === 3 && this.passwordLevel <= 3)
          || (i === 4 && this.passwordLevel <= 4),
        'bg-warning': (i === 1 && this.passwordLevel === 2) || (i === 2 && this.passwordLevel === 2),
        'bg-success': (i >= 1 && i <= 3 && this.passwordLevel >= 3) || (i === 4 && this.passwordLevel >= 4),
      };
    },

    afterValidateCallback(value) {
      if (value === '') {
        this.resetValidationStatus();
        this.passwordLevel = PASSWORD_STRENGTH_LEVEL.None;
        this.$emit('password-status', false);
        return;
      }

      if (value.length <= 10) {
        this.passwordLevel = PASSWORD_STRENGTH_LEVEL.Weak;
      } else {
        const criteriaMet = this.validationFields.filter((e) => e.status === true).length;

        if (criteriaMet === 0) {
          this.passwordLevel = PASSWORD_STRENGTH_LEVEL.Weak;
        } else if (criteriaMet === 1) {
          this.passwordLevel = PASSWORD_STRENGTH_LEVEL.Weak;
        } else if (criteriaMet === 2) {
          this.passwordLevel = PASSWORD_STRENGTH_LEVEL.Regular;
        } else if (criteriaMet === 3) {
          this.passwordLevel = PASSWORD_STRENGTH_LEVEL.Good;
        }

        if (this.passwordLevel === PASSWORD_STRENGTH_LEVEL.Good && value.length >= this.strengthLength.strong) {
          this.passwordLevel = PASSWORD_STRENGTH_LEVEL.Great;
        }
      }

      this.$emit('password-status', this.passwordLevel >= PASSWORD_STRENGTH_LEVEL.Good);
    },

    validateFields(value) {
      this.validationFields = this.validationFields.map((field) => {
        field.status = field.validator(value);
        return field;
      });
    },

    resetValidationStatus() {
      this.validationFields = this.validationFields.map((field) => {
        field.status = null;
        return field;
      });
    },

    checkPasswordStrength(value) {
      if (value === '') {
        this.resetValidationStatus();
        this.passwordLevel = PASSWORD_STRENGTH_LEVEL.None;
        return;
      }

      if (typeof value === 'string') {
        this.validateFields(value);
        this.afterValidateCallback(value);
      } else {
        this.resetValidationStatus();
        this.passwordLevel = PASSWORD_STRENGTH_LEVEL.None;
        this.$emit('password-status', false);
      }
    },

    containsSequence(value) {
      const maxSequenceLength = 3;
      const lowercaseValue = value.toLowerCase();

      for (let i = 0; i <= lowercaseValue.length - maxSequenceLength; i++) {
        const substring = lowercaseValue.substring(i, i + maxSequenceLength);

        if (this.isSequential(substring)) {
          return true;
        }
      }

      return false;
    },

    isSequential(substring) {
      const charCodes = [...substring].map((char) => char.charCodeAt(0));

      return charCodes.every((val, index, arr) => index === 0 || val === arr[index - 1] + 1);
    },
  },

  watch: {
    password(newPassword) {
      this.checkPasswordStrength(newPassword);
    },
  },

  created() {
    this.checkPasswordStrength(this.password);
  },

  validations() {
    return {
      password: {
        required,
        minLength: minLength(this.minPasswordLength),
        maxLength: maxLength(this.maxPasswordLength),
      },
    };
  },
};
</script>

<style lang="stylus" scoped>
.new-password
  position relative
  height auto

  &__informations
    font-size 14px
    margin-top 8px
    display flex
    flex-direction column
    justify-content space-between

    &--steps
      width 100%
      margin-right 2px
      border-radius 0.25rem
      height 3px

  &__validations
    height 60px
    display flex
    flex-direction column
    justify-content space-between

  &__validation-item
    color #707070

    &--error
      color #E24651

    &--correct
      color #27B75D
      text-decoration line-through

  &__tips
    display flex
    margin 14px 0 10px 0

    &--text
      color #707070
      font-weight bold
      margin-right 8px

.bg-danger
  background-color #E7505A

.bg-warning
  background-color #fe9037

.bg-success
  background-color #2BC866

</style>
